/**
 * <biteback-donation-popup> — pre-checkout Lyme research donation ask.
 *
 * Per docs/offer-architecture.md §4 + §6.
 *
 * Trigger: Cart drawer "Secure Checkout" button click.
 * Suppression:
 *   - Skip if dismissed/accepted earlier this session.
 *   - Skip if donation variant is already in the cart.
 *
 * Mechanic: 1-screen modal. Accept → AJAX add donation variant → /checkout.
 * Decline → /checkout immediately. No countdown, no dark patterns. Honest ask.
 */

import { addToCart, fetchCart, on, session } from './biteback.js';

const DECISION_KEY = 'donation:decided';

class BitebackDonationPopup extends HTMLElement {
  connectedCallback() {
    this.variantId = this.dataset.variantId;
    this.checkoutUrl = this.dataset.checkoutUrl || '/checkout';

    this.backdrop = this.querySelector('[data-donation-backdrop]');
    this.panel = this.querySelector('[data-donation-panel]');
    this.acceptBtn = this.querySelector('[data-donation-accept]');
    this.declineBtns = Array.from(this.querySelectorAll('[data-donation-decline]'));

    this.acceptBtn?.addEventListener('click', () => this._accept());
    this.declineBtns.forEach(b => b.addEventListener('click', () => this._decline()));
    this.backdrop?.addEventListener('click', () => this._decline());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.hasAttribute('hidden')) this._decline();
    });

    // Listen for the public open trigger fired by the cart drawer
    this._unbind = on(document, 'biteback:donation-open', () => this._maybeOpen());
  }

  disconnectedCallback() {
    if (typeof this._unbind === 'function') this._unbind();
  }

  async _maybeOpen() {
    // Skip if already decided this session
    if (session.get(DECISION_KEY)) {
      this._proceedToCheckout();
      return;
    }
    // Skip if donation already in cart
    try {
      const cart = await fetchCart();
      const has = cart.items?.some(i => String(i.variant_id) === String(this.variantId));
      if (has) {
        session.set(DECISION_KEY, 'already-in-cart');
        this._proceedToCheckout();
        return;
      }
    } catch (e) {
      // If we can't check the cart, fall through to showing the popup —
      // missing the ask is worse than showing a duplicate decline.
    }
    this._open();
  }

  _open() {
    this.hidden = false;
    this.removeAttribute('inert');
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => this.panel?.classList.add('is-visible'));
  }

  _close() {
    this.panel?.classList.remove('is-visible');
    document.body.style.overflow = '';
    setTimeout(() => {
      this.hidden = true;
      this.setAttribute('inert', '');
    }, 240);
  }

  async _accept() {
    if (this.acceptBtn.disabled) return;
    this.acceptBtn.disabled = true;
    this.acceptBtn.textContent = 'Adding…';
    try {
      await addToCart([{ id: parseInt(this.variantId, 10), quantity: 1 }]);
      session.set(DECISION_KEY, 'accepted');
      this._proceedToCheckout();
    } catch (err) {
      // Don't block checkout if the donation add fails — just proceed.
      console.warn('[biteback] donation add failed:', err);
      session.set(DECISION_KEY, 'accept-failed');
      this._proceedToCheckout();
    }
  }

  _decline() {
    session.set(DECISION_KEY, 'declined');
    this._close();
    this._proceedToCheckout();
  }

  _proceedToCheckout() {
    window.location.href = this.checkoutUrl;
  }
}

customElements.define('biteback-donation-popup', BitebackDonationPopup);
